import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Music2 } from "lucide-react";
import api from "@/lib/api";
import { openOAuth } from "@/lib/tiktok";
import { Button } from "@/components/ui/button";
import { LogoFull } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [tiktokBusy, setTiktokBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("tiktok") === "error") { toast.error(`TikTok sign-in failed (${q.get("reason") || "unknown"}). Try again or use another method.`); window.history.replaceState({}, "", "/login"); }
  }, []);

  const tiktokLogin = async () => {
    setTiktokBusy(true);
    try {
      let src = "", ab = "", ref = "";
      try { src = localStorage.getItem("mf_src") || ""; ab = localStorage.getItem("mf_ab") || ""; ref = localStorage.getItem("mf_ref") || ""; } catch { /* noop */ }
      const { data } = await api.get(`/auth/tiktok/login?src=${encodeURIComponent(src)}&ab=${encodeURIComponent(ab)}&ref=${encodeURIComponent(ref)}`);
      openOAuth(data.url);
    } catch (e) { toast.error(e.response?.data?.detail || "TikTok sign-in is unavailable right now."); setTiktokBusy(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res =
      tab === "login"
        ? await login(form.email, form.password)
        : await register(form.name, form.email, form.password);
    setLoading(false);
    if (res.ok) {
      toast.success(tab === "login" ? "Welcome back" : "Account created");
      navigate("/dashboard", { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid-bg bg-background flex flex-col lg:flex-row">
      <div className="hidden lg:flex flex-1 relative overflow-hidden border-r border-border">
        <img
          src="https://images.unsplash.com/photo-1683064325134-3acfdef9c6d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="sound waves"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="relative z-10 p-16 flex flex-col justify-end">
          <LogoFull width={300} className="mb-8" />
          <h2 className="font-display text-4xl font-extrabold leading-tight max-w-md">
            The AI Soundtrack Engine built for games.
          </h2>
          <p className="text-muted-foreground mt-4 max-w-md">
            Generate loopable scores, stems and adaptive game music from a single prompt.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md glass rounded-2xl p-8"
        >
          <Link to="/" className="lg:hidden mb-6 flex justify-center">
            <LogoFull width={200} />
          </Link>
          <h1 className="font-display text-3xl font-extrabold mb-1">Get started</h1>
          <p className="text-muted-foreground text-sm mb-6">Access your soundtrack studio.</p>

          <Button
            onClick={googleLogin}
            variant="outline"
            className="w-full mb-3 border-border hover:border-primary/50 bg-card"
            data-testid="google-login-button"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4 mr-2" />
            Continue with Google
          </Button>
          <Button
            onClick={tiktokLogin}
            variant="outline"
            disabled={tiktokBusy}
            className="w-full mb-5 border-border hover:border-primary/50 bg-card"
            data-testid="tiktok-login-button"
          >
            {tiktokBusy ? <Loader2 className="animate-spin w-4 h-4 mr-2" /> : <Music2 className="w-4 h-4 mr-2" />}
            Continue with TikTok
          </Button>

          <div className="flex items-center gap-3 mb-5">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground font-mono">OR</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full mb-6 bg-muted">
              <TabsTrigger value="login" data-testid="tab-login">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Sign Up</TabsTrigger>
            </TabsList>

            <form onSubmit={submit} className="space-y-4">
              <TabsContent value="register" className="m-0 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" value={form.name} onChange={set("name")} placeholder="Ada Lovelace" data-testid="register-name-input" required={tab === "register"} />
                </div>
              </TabsContent>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={form.email} onChange={set("email")} placeholder="you@studio.com" data-testid="auth-email-input" required />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between"><Label htmlFor="password">Password</Label>
                  {tab === "login" && <Link to="/reset-password" className="text-xs text-muted-foreground hover:text-primary" data-testid="forgot-password-link">Forgot password?</Link>}</div>
                <Input id="password" type="password" value={form.password} onChange={set("password")} placeholder="••••••••" data-testid="auth-password-input" required />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold glow-primary" data-testid="auth-submit-button">
                {loading ? <Loader2 className="animate-spin" size={18} /> : tab === "login" ? "Sign In" : "Create Account"}
              </Button>
            </form>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
}
